import { fromIsoString, toIsoString } from '../chat/iso-date';
import type { WorkflowWebhookTriggerData } from './types';
import type { SerializedWorkflowWebhookTrigger } from './types';
import { WorkflowTrigger } from './workflow-trigger';
import type { WorkflowTriggerHydration } from './workflow-trigger';

/**
 * Standalone webhook trigger (`workflow_triggers_webhook`). Treat {@link WorkflowWebhookTrigger#secret} as sensitive.
 */
export class WorkflowWebhookTrigger extends WorkflowTrigger<WorkflowWebhookTriggerData> {
  readonly kind = 'webhook' as const;

  private constructor(
    data: WorkflowWebhookTriggerData,
    hydration?: WorkflowTriggerHydration
  ) {
    super(data, hydration);
  }

  get secret(): string {
    return this._data.secret;
  }

  get name(): string {
    return this._data.name;
  }

  get webhookTriggerData(): WorkflowWebhookTriggerData {
    return { ...this._data };
  }

  serialize(): SerializedWorkflowWebhookTrigger {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      name: this._data.name,
      secret: this._data.secret,
      providerKey: this._data.providerKey,
      verificationConfig: this._data.verificationConfig,
      eventKey: this._data.eventKey,
      eventConfig: this._data.eventConfig,
      installStatus: this._data.installStatus,
      enabled: this._data.enabled,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    serialized: SerializedWorkflowWebhookTrigger,
    hydration?: WorkflowTriggerHydration
  ): WorkflowWebhookTrigger {
    const data: WorkflowWebhookTriggerData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      name: serialized.name,
      secret: serialized.secret,
      providerKey: serialized.providerKey,
      verificationConfig: serialized.verificationConfig,
      eventKey: serialized.eventKey,
      eventConfig: serialized.eventConfig,
      installStatus: serialized.installStatus,
      enabled: serialized.enabled,
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new WorkflowWebhookTrigger(data, hydration);
  }
}
